import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Download, Wrench } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What should a plumber invoice include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A plumber invoice should include your name or business name and trade registration number, the client name and job address, a unique invoice number and date, payment due date, a description of the work carried out, labour charges (hours worked and hourly rate), materials and parts used with costs, call-out fee if applicable, applicable VAT or GST, and your payment details.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do plumbers typically charge for their work?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Plumbers commonly charge a call-out fee for attending the job, then an hourly labour rate for the time spent, plus materials at cost or with a markup. For larger jobs, a fixed project quote is common. Emergency or out-of-hours work typically carries a higher rate. Always itemise labour and materials separately on your invoice so the client can clearly see what they are paying for.',
      },
    },
    {
      '@type': 'Question',
      name: 'Should plumbers charge VAT or GST?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes, if you are registered. In the UK, VAT at 20% applies to plumbing services once your turnover exceeds £90,000 (reduced 5% VAT applies to certain energy-saving installations). In Australia, 10% GST applies if turnover exceeds AUD $75,000. In India, plumbing services attract 18% GST for registered businesses. In the US, plumbing labour is generally not taxable in most states, though materials may be subject to sales tax.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I add a markup on materials on a plumber invoice?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. It is standard practice for plumbers and tradespeople to add a markup on materials (typically 10–30%) to cover sourcing time, transport, and holding costs. Be transparent about this by labelling the line item clearly as "Materials" with the total including your markup, rather than showing the trade cost. If a client asks for receipts, decide in advance whether your markup is disclosed.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is this plumber invoice template free?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic provides a free plumber invoice template. Fill in your job details online and download a professional PDF instantly. Free for up to 5 invoices per month with no sign-up required.',
      },
    },
  ],
};

export default function PlumberInvoiceTemplatePage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Plumber Invoice Template: Free PDF for Plumbers & Tradespeople | Invilic"
        description="Free plumber invoice template. Bill for labour, materials, call-out fees, and VAT/GST. Download a professional PDF instantly. No sign-up needed."
        canonical="https://www.invilic.com/plumber-invoice-template"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-16">
          <span className="inline-block bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            Free Template
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Plumber Invoice Template
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            A professional invoice template for plumbers and plumbing businesses. Bill for labour, materials, and call-out fees. Download a clean PDF in seconds — no account needed.
          </p>
          <Link
            to="/create/freelancer"
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-blue-600/20 transition-all duration-200 text-lg"
          >
            Use Free Template <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            Built for Every Type of Plumbing Work
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { title: 'Residential plumbers', desc: 'Bill homeowners for repairs, installations, boiler work, and emergency call-outs.' },
              { title: 'Commercial plumbers', desc: 'Invoice businesses and landlords for large-scale plumbing installations and maintenance contracts.' },
              { title: 'Gas & heating engineers', desc: 'Bill for boiler servicing, gas safety checks, central heating installations, and repairs.' },
              { title: 'Drainage & specialist plumbers', desc: 'Issue invoices for drain clearance, CCTV surveys, and specialist plumbing projects.' },
            ].map(({ title, desc }) => (
              <div key={title} className="flex gap-4 p-5 rounded-xl border border-gray-100 dark:border-gray-800">
                <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center flex-shrink-0">
                  <Wrench className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900 dark:text-white text-sm mb-1">{title}</div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            What's in the Plumber Invoice Template
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              'Business name and trade registration',
              'Client name and job address',
              'Invoice number and job date',
              'Payment due date',
              'Work description per line item',
              'Labour hours and hourly rate',
              'Call-out or emergency fee',
              'Materials and parts with costs',
              'Subtotal, VAT / GST and discount',
              'Total amount due',
              'Bank details or payment link',
              'Guarantee or warranty notes',
            ].map(f => (
              <div key={f} className="flex items-center gap-3 p-4 rounded-xl border border-blue-100 dark:border-blue-800/30 bg-blue-50 dark:bg-blue-900/10">
                <CheckCircle className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-300">{f}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            How to Create Your Plumbing Invoice
          </h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { n: '1', title: 'Open the template', desc: 'Click "Use Free Template" above. No software to install, no account needed.' },
              { n: '2', title: 'Fill in your job', desc: 'Enter your work, hours, materials, call-out fee, and any VAT or GST.' },
              { n: '3', title: 'Download PDF', desc: 'Download a professional PDF invoice ready to hand to your client or send by email.' },
            ].map(({ n, title, desc }) => (
              <div key={n} className="text-center p-6 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
                <div className="w-10 h-10 rounded-full bg-blue-600 text-white font-bold text-lg flex items-center justify-center mx-auto mb-4">
                  {n}
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
            Plumber Invoice Template: FAQs
          </h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                  {acceptedAnswer.text}
                </p>
              </details>
            ))}
          </div>
        </section>

        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Create Your Plumbing Invoice Now for Free
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Professional PDF. No account required. Ready in 2 minutes.</p>
          <Link
            to="/create/freelancer"
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-blue-600/20 transition-all duration-200 text-lg"
          >
            <Download className="w-5 h-5" /> Get Free Plumber Invoice Template
          </Link>
        </section>
      </main>
    </div>
  );
}
